import React, { useCallback, useRef } from 'react'
import ReactDatePicker from 'react-datepicker'
import ReactDOM from 'react-dom'
import InputMask from 'react-input-mask'

import Moment from 'moment'

// react-datepicker requires customInput to support ref forwarding so it can
// control focus and open/close the calendar programmatically.
const MaskedDateInput = React.forwardRef((props, ref) => <InputMask mask="99/99/9999" ref={ref} {...props} />)
MaskedDateInput.displayName = 'MaskedDateInput'

export default ({ name, id, value, onChange, dataFormat = 'YYYY-MM-DD', portalId, ...props }) => {
  const datePickerRef = useRef(null)

  const popperContainer = useCallback(
    ({ children }) => {
      if (typeof document === 'undefined') return children
      const target = (portalId && document.getElementById(portalId)) || document.body
      return ReactDOM.createPortal(<div className="date-picker-portal">{children}</div>, target)
    },
    [portalId],
  )

  const handleOnChange = value => {
    const valueToUse = (() => {
      if (!value) return null
      if (dataFormat) return Moment(value).format(dataFormat)
      return value
    })()

    onChange({
      target: {
        type: 'date-picker',
        name,
        value: valueToUse,
      },
    })
  }

  // Handle raw typed input where the user didn't use the calendar picker.
  //
  // The mask enforces MM/DD/YYYY (9 digits), but power users often type a bare
  // digit string. Two cases are handled:
  //
  //   6 digits (MMDDYY) — e.g. "041526" or the incomplete masked value "04/15/26__"
  //     → parsed as MMDDYY using Moment's 2-digit year pivot:
  //       00–68 → 2000–2068, 69–99 → 1969–1999 (JS standard)
  //     ⚠ NOTE: For Date of Birth, advise users to enter 4-digit years.
  //       "52" is interpreted as 2052, not 1952.
  //
  //   8 digits (MMDDYYYY) — e.g. "04152026" or "04/15/2026" with slashes
  //     → the mask formats this naturally; the DatePicker parser handles it.
  //     onChangeRaw is a no-op in this case (8-digit check not needed here).
  const handleChangeRaw = e => {
    const digits = (e.target.value || '').replace(/\D/g, '')
    if (digits.length === 0) {
      handleOnChange(null)
    } else if (digits.length === 6) {
      const m = Moment(digits, 'MMDDYY', true)
      if (m.isValid()) handleOnChange(m.toDate())
    }
  }

  const valueAsDate = (() => {
    if (!value) return null
    // Try the declared storage format first (strict) — this handles date-only
    // strings like "2026-05-10" that were produced by handleOnChange.
    const strict = Moment(value, dataFormat, true)
    if (strict.isValid()) return strict.toDate()
    // Fall back to Moment's ISO 8601 parser for full datetime strings like
    // "2026-05-10T00:00:00Z". These arise because date-only fields are stored
    // as DateTime? (not DateOnly?) in the API, so SQL Server always round-trips
    // them with a midnight UTC time component. See #448 for the proper fix
    // (migrate date-only columns to DateOnly? / SQL `date` type).
    const iso = Moment(value)
    return iso.isValid() ? iso.toDate() : null
  })()

  return (
    <div className="input-group date-picker">
      <ReactDatePicker
        ref={datePickerRef}
        id={id}
        {...props}
        customInput={<MaskedDateInput />}
        dateFormat="MM/dd/yyyy"
        className="form-control"
        selected={valueAsDate}
        onChange={handleOnChange}
        onChangeRaw={handleChangeRaw}
        popperContainer={popperContainer}
        isClearable
      />
      <div
        className="input-group-append"
        onClick={() => datePickerRef.current.setOpen(true)}
        style={{ cursor: 'pointer' }}
        role="button"
        tabIndex="0"
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            datePickerRef.current.setOpen(true)
          }
        }}
      >
        <span id="calendarIcon" className="fas fa-calendar input-group-text"></span>
      </div>
    </div>
  )
}
